// src/components/BeatHighlighter.jsx
import { useState, useEffect, useRef } from "react";

function BeatHighlighter({ audioCtx, scheduledBeatsRef, leadTimeMs = 30, isPlaying }) {
  const rafRef = useRef(null);
  const activeIndexRef = useRef(-999);
  const lastUpdateTimeRef = useRef(0);
  
  // Clear all highlights when component mounts/unmounts
  useEffect(() => {
    clearAllHighlights();
    return () => clearAllHighlights();
  }, []);
  
  // Clear function that can be called anywhere
  function clearAllHighlights() {
    document.querySelectorAll("[data-highlighted='true']").forEach(el => {
      el.style.backgroundColor = "";
      el.removeAttribute("data-highlighted");
    });
  }
  
  // Reset state when isPlaying changes
  useEffect(() => {
    if (isPlaying) {
      activeIndexRef.current = -999;
    } else {
      clearAllHighlights();
    }
  }, [isPlaying]);
  
  useEffect(() => {
    if (!isPlaying) return;
    
    function loop() {
      const nowSec = audioCtx.currentTime;
      const schedArr = scheduledBeatsRef.current;
      
      // Skip if no beats are scheduled
      if (!Array.isArray(schedArr) || schedArr.length === 0) {
        rafRef.current = requestAnimationFrame(loop);
        return;
      }
      
      // Find the current beat
      let newBeatIndex = -1;
      
      // Simple linear scan for the current beat
      for (let i = 0; i < schedArr.length; i++) {
        const adjustedTime = schedArr[i].startTimeSec - leadTimeMs/1000;
        if (adjustedTime > nowSec) {
          newBeatIndex = i - 1;
          break;
        }
      }
      
      // If loop finished without finding a future beat, use the last one
      if (newBeatIndex === -1) {
        newBeatIndex = schedArr.length - 1;
      }
      
      // Clamp to valid range
      if (newBeatIndex < 0) newBeatIndex = 0;
      if (newBeatIndex >= schedArr.length) newBeatIndex = schedArr.length - 1;
      
      // Force update at least every 100ms for more responsiveness
      const forceUpdate = nowSec - lastUpdateTimeRef.current > 0.1;
      
      // Update highlighting if needed
      if (newBeatIndex !== activeIndexRef.current || forceUpdate) {
        // Save the new active index
        activeIndexRef.current = newBeatIndex;
        lastUpdateTimeRef.current = nowSec;
        
        // FIRST: Clear all existing highlights
        clearAllHighlights();
        
        // THEN: Add new highlight
        if (newBeatIndex >= 0 && newBeatIndex < schedArr.length) {
          const { measureIndex, beatIndex } = schedArr[newBeatIndex];
          const beatId = `m${measureIndex}-b${beatIndex}`;
          
          const newEl = document.querySelector(`[data-beat-id="${beatId}"]`);
          if (newEl) {
            // Apply styles directly - no transition for snappier updates
            newEl.style.backgroundColor = "#ffcc00"; // brighter yellow for better contrast
            newEl.setAttribute("data-highlighted", "true");
          }
        }
      }
      
      rafRef.current = requestAnimationFrame(loop);
    }
    
    rafRef.current = requestAnimationFrame(loop);
    
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [audioCtx, scheduledBeatsRef, leadTimeMs, isPlaying]);
  
  return null;
}
export default BeatHighlighter;
